import { useEffect, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Kudos } from '@/components/Kudos';
import { StreakFlame } from '@/components/StreakFlame';
import { useCalm } from './time';

/**
 * The ward whiteboard: the kudos line in confident interface text, with the streak flame pinned underneath.
 * `tone` picks the ink: teal with a lime highlight when right, navy when wrong, faded when there was no answer.
 */
export function Whiteboard({ line, tone = 'right', children, className = '' }: { line: string; tone?: 'right' | 'wrong' | 'quiet'; children?: ReactNode; className?: string }) {
  const ink = tone === 'right' ? 'text-brand-strong' : tone === 'wrong' ? 'text-ink' : 'text-ink-2';
  return (
    <figure className={`relative overflow-hidden rounded-tile border border-line bg-surface px-4 pb-4 pt-3.5 shadow-card ${className}`}>
      <Kudos key={line} text={line} delayMs={250} stepMs={70} underline={tone === 'right' ? 'lime' : false} className={`text-[1.4rem] leading-snug ${ink}`} />
      {children && <div className="mt-3 flex flex-wrap items-center gap-2">{children}</div>}
    </figure>
  );
}

/** How long a lost streak's flame stays lit on screen before it is snuffed out, so the loss is seen happening. */
export const SNUFF_AFTER_MS = 900;

/** The streak flame and its line. `lost` shows the flame lit, then snuffs it out with a puff, for the answer that ends a run. */
export function StreakChip({ text, n, lost = false }: { text: string; n: number; lost?: boolean }) {
  const reduce = useCalm();
  const [out, setOut] = useState(false);
  useEffect(() => {
    if (!lost) { setOut(false); return; }
    const t = window.setTimeout(() => setOut(true), reduce ? 0 : SNUFF_AFTER_MS);
    return () => window.clearTimeout(t);
  }, [lost, reduce]);
  // The words carry the meaning; the flame is their visual twin.
  const flame = <span aria-hidden="true" className="inline-flex"><StreakFlame n={n} size={17} snuffed={lost && out} showCount={false} /></span>;
  if (lost) return (
    <span data-snuffed={out ? 'yes' : 'no'} className="inline-flex items-center gap-1.5 rounded-md bg-surface-2 px-3 py-1.5 text-sm font-semibold text-ink-2">
      {flame}{text}
    </span>
  );
  return (
    <motion.span initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.6, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.55, type: 'spring', stiffness: 520, damping: 18 }}
      className="inline-flex items-center gap-1.5 rounded-md bg-lime px-3 py-1.5 text-sm font-bold text-ink">
      {flame}{text}
    </motion.span>
  );
}
